import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const initialConsult: ContentBlock<"procedure"> = {
  id: makeId("proc-initial-consult"),
  kind: "procedure",
  tags: ["clinic-visit", "both-partners", "pre-cycle", "practical"],
  title: "Initial Consultation",
  subtitle: "First meeting with a reproductive endocrinologist",
  body: {
    procedureName: "Initial Fertility Consultation",
    category: "consultation",
    description:
      "Your first appointment with a reproductive endocrinologist (RE) to review your medical and reproductive history, discuss goals, and create a diagnostic or treatment plan. This is the most important visit for setting the direction of your fertility care.",
    howItWorks:
      "The consultation typically lasts 45-60 minutes and is largely a conversation. The RE will review your medical history, menstrual cycle patterns, any prior pregnancies or fertility treatments, surgical history, and lifestyle factors. If you have a partner, their history will be reviewed as well. The physician may perform a brief transvaginal ultrasound to assess your ovaries and uterus. Based on the discussion, the RE will recommend initial diagnostic tests (such as bloodwork, semen analysis, or an HSG) and outline possible treatment paths. Many patients leave with a clear next-steps plan and lab orders to complete before the follow-up visit.",
    duration: "45-60 minutes",
    costRange: { low: 250, high: 600 },
    currency: "USD",
    prepInstructions: [
      "Gather your medical records: prior lab results, imaging, surgical reports, and treatment summaries",
      "Prepare a list of all current medications and supplements",
      "Write down your questions and priorities so you don't forget anything",
      "If applicable, bring your partner, their history is part of the evaluation",
      "Check whether your insurance requires a referral and bring it if so",
    ],
    whatToExpect: [
      "A detailed conversation about your reproductive and medical history",
      "Questions about menstrual cycle regularity, sexual history, and lifestyle",
      "A possible transvaginal ultrasound during the visit",
      "A discussion of potential diagnoses and treatment options",
      "Lab orders and a timeline for next steps",
      "Bring photo ID and insurance card",
      "Bring prior medical records, lab results, and imaging reports",
      "Bring a list of current medications and supplements",
      "Bring your written list of questions",
      "Bring your partner, if applicable",
    ],
    forWhom: "both",
    frBody: {
      procedureName: "Première consultation",
      description:
        "Premier rendez-vous avec un médecin de la reproduction pour reprendre vos antécédents médicaux et reproductifs, discuter de vos objectifs et établir un plan de bilan ou de traitement. C'est la consultation la plus importante pour donner la direction de votre prise en charge.",
      howItWorks:
        "La consultation dure généralement 45 à 60 minutes et repose largement sur l'échange. Le médecin reprend vos antécédents médicaux, le profil de vos cycles, les éventuelles grossesses ou traitements antérieurs, les antécédents chirurgicaux et les facteurs de mode de vie. Si vous avez un partenaire, ses antécédents sont également examinés. Le médecin peut réaliser une échographie endovaginale brève pour évaluer les ovaires et l'utérus. À partir de l'entretien, il propose les premiers examens à réaliser (bilan sanguin, spermogramme, HSG) et esquisse les options thérapeutiques. La plupart des patient·es repartent avec un plan clair et des ordonnances pour les examens à effectuer avant la consultation de suivi.",
      duration: "45 à 60 minutes",
      prepInstructions: [
        "Rassemblez votre dossier médical : résultats d'analyses, imagerie, comptes rendus opératoires et traitements antérieurs",
        "Préparez la liste de vos traitements et compléments en cours",
        "Notez vos questions et priorités pour n'en oublier aucune",
        "Le cas échéant, venez avec votre partenaire ; ses antécédents font partie du bilan",
        "Vérifiez si votre mutuelle exige une lettre d'orientation et apportez-la si oui",
      ],
      whatToExpect: [
        "Échange détaillé sur vos antécédents médicaux et reproductifs",
        "Questions sur la régularité des cycles, l'histoire sexuelle et le mode de vie",
        "Éventuelle échographie endovaginale durant la consultation",
        "Discussion des diagnostics possibles et des options thérapeutiques",
        "Ordonnances d'examens et calendrier des prochaines étapes",
        "Apportez une pièce d'identité et votre carte Vitale/mutuelle",
        "Apportez les dossiers médicaux, résultats d'analyses et imageries antérieurs",
        "Apportez la liste des traitements et compléments en cours",
        "Apportez votre liste de questions",
        "Venez avec votre partenaire si concerné",
      ],
    },
  },
  sources: [
    {
      title: "Fertility evaluation of infertile women: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2021,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/fertility-evaluation-of-infertile-women-a-committee-opinion-2021/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(initialConsult);
